import { Card } from "@/components/ui/card";
import { ChevronLeft, ChevronRight, X, ArrowLeft, ArrowRight, ZoomIn, ZoomOut } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { useState, useEffect, useRef } from "react";

// Import delle immagini del menu
import menuPagina1 from "@/assets/menu-pagina-1.webp";
import menuPagina2 from "@/assets/menu-pagina-2.webp";
import menuPagina3 from "@/assets/menu-pagina-3.webp";
import menuPagina4 from "@/assets/menu-pagina-4.webp";

const MenuSection = () => {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const [zoomLevel, setZoomLevel] = useState(100); // Zoom level in percentage
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 }); // Pan position
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imageRef = useRef<HTMLImageElement>(null);

  // Array delle immagini del menu
  const menuImages = [
    menuPagina1,
    menuPagina2,
    menuPagina3,
    menuPagina4
  ];

  const openImageModal = (index: number) => {
    setSelectedImage(index);
    setZoomLevel(100); // Reset zoom when opening modal
    setPanPosition({ x: 0, y: 0 }); // Reset pan position
  };

  const closeImageModal = () => {
    setSelectedImage(null);
    setZoomLevel(100); // Reset zoom when closing modal
    setPanPosition({ x: 0, y: 0 }); // Reset pan position
  };

  const nextImage = () => {
    if (selectedImage !== null) {
      setSelectedImage((selectedImage + 1) % menuImages.length);
      setZoomLevel(100); // Reset zoom when changing image
      setPanPosition({ x: 0, y: 0 }); // Reset pan position
    }
  };

  const previousImage = () => {
    if (selectedImage !== null) {
      setSelectedImage(selectedImage === 0 ? menuImages.length - 1 : selectedImage - 1);
      setZoomLevel(100); // Reset zoom when changing image
      setPanPosition({ x: 0, y: 0 }); // Reset pan position
    }
  };

  const zoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 25, 300)); // Max 300% zoom
  };

  const zoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 25, 25)); // Min 25% zoom
  };

  // Panning functionality
  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoomLevel > 100) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - panPosition.x,
        y: e.clientY - panPosition.y
      });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && zoomLevel > 100) {
      setPanPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch events for mobile
  const handleTouchStart = (e: React.TouchEvent) => {
    if (zoomLevel > 100 && e.touches.length === 1) {
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - panPosition.x,
        y: e.touches[0].clientY - panPosition.y
      });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isDragging && zoomLevel > 100 && e.touches.length === 1) {
      // Remove preventDefault to avoid the warning
      setPanPosition({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y
      });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Reset pan when zoom changes
  useEffect(() => {
    if (zoomLevel <= 100) {
      setPanPosition({ x: 0, y: 0 });
    }
  }, [zoomLevel]);

  // Add touch event listeners with passive: false
  useEffect(() => {
    const imageElement = imageRef.current;
    if (!imageElement) return;

    const handleTouchStartNative = (e: TouchEvent) => {
      if (zoomLevel > 100 && e.touches.length === 1) {
        setIsDragging(true);
        setDragStart({
          x: e.touches[0].clientX - panPosition.x,
          y: e.touches[0].clientY - panPosition.y
        });
      }
    };

    const handleTouchMoveNative = (e: TouchEvent) => {
      if (isDragging && zoomLevel > 100 && e.touches.length === 1) {
        e.preventDefault(); // This will work with passive: false
        setPanPosition({
          x: e.touches[0].clientX - dragStart.x,
          y: e.touches[0].clientY - dragStart.y
        });
      }
    };

    const handleTouchEndNative = () => {
      setIsDragging(false);
    };

    // Add event listeners with passive: false
    imageElement.addEventListener('touchstart', handleTouchStartNative, { passive: false });
    imageElement.addEventListener('touchmove', handleTouchMoveNative, { passive: false });
    imageElement.addEventListener('touchend', handleTouchEndNative, { passive: false });

    return () => {
      imageElement.removeEventListener('touchstart', handleTouchStartNative);
      imageElement.removeEventListener('touchmove', handleTouchMoveNative);
      imageElement.removeEventListener('touchend', handleTouchEndNative);
    };
  }, [zoomLevel, isDragging, panPosition, dragStart]);

  return (
    <section id="menu" className="section-padding bg-background">
      <div className="container-custom">
        <article className="text-center mb-12">
          <h2 className="text-section-title text-primary mb-4">
            Menu Cucina <span className="text-gold">Umbra Tradizionale</span>
          </h2>
          <p className="text-body-large text-muted-foreground max-w-3xl mx-auto font-serif mb-8">
            Scopri il nostro <strong>menu di cucina umbra autentica</strong>: pesce fresco del <strong>Lago Trasimeno</strong>, tartufo nero pregiato, pasta fatta in casa e carni alla griglia. Ogni piatto racconta la tradizione gastronomica umbra con ingredienti selezionati e ricette locali tramandate di generazione in generazione.
          </p>
        </article>

        <div className="relative">
          <Carousel
            opts={{
              align: "start",
              slidesToScroll: 1,
            }}
            className="w-full"
          >
            <CarouselContent className="-ml-2 md:-ml-4">
              {menuImages.map((image, index) => (
                <CarouselItem key={index} className="pl-2 md:pl-4 basis-full md:basis-1/2">
                  <div className="relative group cursor-pointer" onClick={() => openImageModal(index)}>
                    <img
                      src={image}
                      alt={`Menu Ristorante Da Armando - Cucina umbra autentica pagina ${index + 1}: pesce del lago Trasimeno, tartufo nero pregiato, pasta fatta in casa e piatti tradizionali`}
                      loading="lazy"
                      className="w-full h-auto rounded-lg shadow-lg group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 rounded-lg"></div>
                  </div>
                  <div className="mt-4 text-center">
                    <p className="text-sm text-muted-foreground">Pagina {index + 1}</p>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="flex -left-2 md:-left-4" />
            <CarouselNext className="flex -right-2 md:-right-4" />
          </Carousel>
        </div>
      </div>

      {/* Modal per visualizzare l'immagine a schermo intero */}
      {selectedImage !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 pt-16" onClick={closeImageModal}>
          <div className="relative max-w-4xl max-h-[85vh] mx-4">
            <button 
              onClick={closeImageModal}
              aria-label="Chiudi anteprima menu"
              className="absolute -top-12 right-0 text-white hover:text-gold transition-colors z-10"
            >
              <X size={32} />
            </button>
            
            {/* Pulsante precedente */}
            <button 
              onClick={(e) => {
                e.stopPropagation();
                previousImage();
              }}
              aria-label="Pagina precedente del menu"
              className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gold transition-colors z-10 bg-black/50 rounded-full p-2"
            >
              <ArrowLeft size={24} />
            </button>
            
            {/* Pulsante successivo */}
            <button 
              onClick={(e) => {
                e.stopPropagation();
                nextImage();
              }}
              aria-label="Pagina successiva del menu"
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gold transition-colors z-10 bg-black/50 rounded-full p-2"
            >
              <ArrowRight size={24} />
            </button>

            {/* Pulsanti zoom */}
            <div className="absolute bottom-4 right-4 flex gap-2 z-10">
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  zoomOut();
                }}
                aria-label="Riduci zoom"
                className="text-white hover:text-gold transition-colors bg-black/50 rounded-full p-2 disabled:opacity-50"
                disabled={zoomLevel <= 40}
              >
                <ZoomOut size={20} />
              </button>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  zoomIn();
                }}
                aria-label="Aumenta zoom"
                className="text-white hover:text-gold transition-colors bg-black/50 rounded-full p-2 disabled:opacity-50"
                disabled={zoomLevel >= 300}
              >
                <ZoomIn size={20} />
              </button>
            </div>
            
            <img
              ref={imageRef}
              src={menuImages[selectedImage]}
              alt={`Menu Ristorante Da Armando - Cucina umbra autentica pagina ${selectedImage + 1}: pesce del lago Trasimeno, tartufo nero pregiato, pasta fatta in casa e piatti tradizionali`}
              className="w-full h-auto max-h-[85vh] object-contain rounded-lg shadow-2xl cursor-grab active:cursor-grabbing"
              style={{ 
                transform: `scale(${zoomLevel / 100}) translate(${panPosition.x}px, ${panPosition.y}px)`,
                cursor: zoomLevel > 100 ? (isDragging ? 'grabbing' : 'grab') : 'default'
              }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            />
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white bg-black/50 px-4 py-2 rounded-lg">
              <p className="text-sm">Pagina {selectedImage + 1} di {menuImages.length} • Zoom {zoomLevel}%</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default MenuSection;